import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BONUS_MODES, bonusIsActiveOn, bonusPeriodsInRange, fullName } from "@torre/team";
import { localSlot } from "@torre/work-hours";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import { formatMoney } from "../theme";
import {
  fetchTeamBonuses,
  createTeamBonus,
  updateTeamBonus,
  deleteTeamBonus,
  fetchTeamMembers,
} from "../services/teamService";
import type { TeamMember, TeamMemberBonus } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// ---------------------------------------------------------------------------
// Bonos individuales con vigencia (#1365).
// ---------------------------------------------------------------------------
// Un extra de UNA persona con fecha de inicio y de fin obligatorias:
//   • por periodo: el monto se paga en CADA periodo de nómina de la vigencia;
//   • por rango: el monto es el TOTAL de la vigencia, repartido entre sus
//     periodos.
// Lo que se paga lo decide el servidor (corrida de pagos y recibo); aquí se
// capturan y se ve cuál está vigente, cuál venció y cuál no ha empezado.
// ---------------------------------------------------------------------------
export default function BonusesSection() {
  const isStaffAdmin = useIsStaffAdmin();
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<Row[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  /** Hoy en CDMX, la misma referencia que el backend. */
  const today = useMemo(() => localSlot(new Date().toISOString())?.date ?? new Date().toISOString().slice(0, 10), []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKey(null);
    try {
      const { items } = await fetchTeamBonuses();
      setRows(items as unknown as Row[]);
    } catch {
      setErrorKey("team.loadError");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Sólo quien administra da de alta: a esa persona se le ofrece el padrón.
  useEffect(() => {
    if (!isStaffAdmin) return;
    let alive = true;
    (async () => {
      try {
        const { items } = await fetchTeamMembers({ status: "active" });
        if (alive) setMembers(items ?? []);
      } catch {
        if (alive) setMembers([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isStaffAdmin]);

  /** Vigente / vencido / por iniciar, según hoy. */
  const statusOf = (r: Row): "active" | "expired" | "upcoming" | "inactive" => {
    if (r.active === false) return "inactive";
    const b = r as unknown as TeamMemberBonus;
    if (bonusIsActiveOn(b, today)) return "active";
    return String(b.endDate) < today ? "expired" : "upcoming";
  };

  const STATUS_TONES: Record<string, string> = {
    active: "bg-emerald-500/15 text-emerald-500",
    expired: "bg-gray-400/15 text-gray-500",
    upcoming: "bg-sky-500/15 text-sky-500",
    inactive: "bg-red-500/15 text-red-500",
  };

  const columns: Column[] = [
    { key: "teamMemberName", label: t("team.fields.teamMember") },
    { key: "concept", label: t("team.fields.bonusConcept") },
    {
      key: "mode",
      label: t("team.fields.bonusMode"),
      render: (r) => t(`team.bonusModes.${String(r.mode || "per_period")}`),
    },
    {
      key: "amount",
      label: t("team.fields.bonusAmount2"),
      align: "right",
      render: (r) => {
        const money = formatMoney(r.amount, (r.currency as string | null) ?? null);
        const mode = String(r.mode || "per_period");
        if (mode === "range") {
          const periods = bonusPeriodsInRange(String(r.startDate), String(r.endDate), "semimonthly");
          return (
            <span data-testid="bonus-amount" className="inline-flex flex-col items-end leading-tight">
              <span className="font-medium">{t("team.bonuses.rangeShort", { amount: money })}</span>
              <span className="text-[11px] opacity-70">≈ {formatMoney(Number(r.amount) / periods, (r.currency as string | null) ?? null)} × {periods}</span>
            </span>
          );
        }
        return <span data-testid="bonus-amount" className="font-medium">{t("team.bonuses.perPeriodShort", { amount: money })}</span>;
      },
    },
    {
      key: "validity",
      label: t("team.fields.validity"),
      render: (r) => t("team.bonuses.validity", { from: String(r.startDate ?? "—"), to: String(r.endDate ?? "—") }),
    },
    {
      key: "status",
      label: t("team.fields.status"),
      align: "center",
      render: (r) => {
        const s = statusOf(r);
        return (
          <span data-testid="bonus-status" className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONES[s]}`}>
            {s === "inactive" ? t("team.no") : t(`team.bonuses.${s}`)}
          </span>
        );
      },
    },
  ];

  const fields: FormField[] = [
    {
      key: "teamMemberId",
      label: t("team.fields.teamMember"),
      type: "select",
      required: true,
      options: members.map((m) => ({ value: m.id, label: fullName(m) || m.id })),
      hint: t("team.hints.bonusMember"),
      // La moneda del bono es la de la persona, salvo que se cambie a mano.
      derive: (value) => {
        const m = members.find((x) => x.id === value);
        return m && m.currency ? { currency: String(m.currency) } : undefined;
      },
    },
    { key: "concept", label: t("team.fields.bonusConcept"), required: true, hint: t("team.hints.bonusConcept") },
    {
      key: "mode",
      label: t("team.fields.bonusMode"),
      type: "select",
      required: true,
      defaultValue: "per_period",
      options: BONUS_MODES.map((m) => ({ value: m, label: t(`team.bonusModes.${m}`) })),
      hint: t("team.hints.bonusMode"),
    },
    { key: "amount", label: t("team.fields.bonusAmount2"), type: "number", required: true, hint: t("team.hints.bonusAmount") },
    {
      key: "currency",
      label: t("team.fields.currency"),
      type: "select",
      options: [
        { value: "MXN", label: "MXN" },
        { value: "USD", label: "USD" },
      ],
    },
    { key: "startDate", label: t("team.fields.startDate"), type: "date", required: true, hint: t("team.hints.bonusValidity") },
    { key: "endDate", label: t("team.fields.endDate"), type: "date", required: true },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  // Aviso en vivo: fin antes del inicio no se guarda (el servidor también lo rechaza).
  const formFooter = (values: Record<string, string>) => {
    if (values.startDate && values.endDate && values.endDate < values.startDate) {
      return (
        <p data-testid="bonus-dates-error" className="rounded-xl border border-red-500 p-3 text-xs text-red-500">
          {t("team.bonuses.endBeforeStart")}
        </p>
      );
    }
    return null;
  };

  const toPayload = (values: Record<string, string>): Partial<TeamMemberBonus> => ({
    teamMemberId: values.teamMemberId,
    concept: values.concept,
    mode: (values.mode || "per_period") as TeamMemberBonus["mode"],
    amount: values.amount ? Number(values.amount) : 0,
    currency: values.currency || null,
    startDate: values.startDate,
    endDate: values.endDate,
    notes: values.notes || null,
  });

  const guard = (values: Record<string, string>) => {
    if (values.startDate && values.endDate && values.endDate < values.startDate) {
      throw new Error(t("team.bonuses.endBeforeStart"));
    }
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("team.bonuses.title")}
      subtitle={t("team.bonuses.subtitle")}
      icon="ri-gift-line"
      columns={columns}
      fields={fields}
      formFooter={formFooter}
      rows={rows}
      loading={loading}
      error={errorKey ? t(errorKey) : null}
      searchKey="teamMemberName"
      addLabel={t("team.bonuses.new")}
      onCreate={async (values) => {
        guard(values);
        await createTeamBonus(toPayload(values));
        await load();
      }}
      onUpdate={async (id, values) => {
        guard(values);
        await updateTeamBonus(id, toPayload(values));
        await load();
      }}
      onDelete={async (id) => {
        await deleteTeamBonus(id);
        await load();
      }}
    />
  );
}
